import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, CircularProgress, Alert, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { PlayArrow as PlayArrowIcon, Insights as InsightsIcon } from '@mui/icons-material';
import GuidedEntryRunChart from '../GuidedEntryRunChart';
import { listMeasurements, runCapabilitySnapshot } from './partsApi';
import { mapConformance, VerdictChip, fmtNum } from './partsFormat';
import { apiErrorMessage } from '../../api/apiError';

// ---------------------------------------------------------------------------
// FeatureInspectionView — the feature page's DEFAULT view (doc §5:400):
// last-N readings with a per-reading verdict, % in spec, and a run chart with
// spec lines only. Inspector vocabulary — no sigma/Cpk/control-limit language
// anywhere in this default view.
//
// The "windowed capability snapshot" (also named Phase 1, doc §7/§2.5) is a
// SEPARATE, explicitly-triggered action below — it is not shown by default and
// never blends into the verdict/chip framing above. It is a manual button here,
// not a nudge (the nudge ladder that would auto-suggest it is Phase 2).
// ---------------------------------------------------------------------------

const headCellSx = {
    color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid rgba(255,255,255,0.1)', py: 0.75, px: 1.5,
};

function FeatureInspectionView({ part, feature, stream, onRecordMeasurements }) {
    const [measurements, setMeasurements] = useState([]);
    const [pctInSpec, setPctInSpec] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [snapshot, setSnapshot] = useState(null);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError, setSnapshotError] = useState(null);

    const specLimits = { usl: feature?.usl, lsl: feature?.lsl, target: feature?.nominal };

    const fetchMeasurements = useCallback(async () => {
        if (!feature?.id) return;
        setLoading(true);
        setError(null);
        try {
            const { ok, status, data } = await listMeasurements(feature.id, {
                process: stream?.process_label,
                limit: 25,
            });
            if (!ok) {
                setError(apiErrorMessage(status, data, 'Could not load readings.'));
                setMeasurements([]);
                setPctInSpec(null);
                return;
            }
            // Real GET /api/features/:id/measurements shape (backend/measurementRoutes.js
            // lines ~538-545; pinned by backend/tests/measurementRoutes.test.js "verdict
            // annotation (conformance)"): { points: [...], pctInSpec, ... } — NOT
            // `data.measurements`, and points are camelCase (measuredAt/attrValue/...).
            setMeasurements(Array.isArray(data.points) ? data.points : []);
            setPctInSpec(typeof data.pctInSpec === 'number' ? data.pctInSpec : null);
        } catch (e) {
            setError('Network error loading readings.');
        } finally {
            setLoading(false);
        }
    }, [feature?.id, stream?.process_label]);

    useEffect(() => { fetchMeasurements(); }, [fetchMeasurements]);

    // Most-recent-first for the readings list; chronological (ascending id) for the run chart.
    const recentFirst = measurements;
    const chronological = [...measurements].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    const chartValues = chronological.map((m) => Number(m.value)).filter((v) => Number.isFinite(v));

    const handleRunSnapshot = async () => {
        setSnapshotLoading(true);
        setSnapshotError(null);
        try {
            const { ok, status, data } = await runCapabilitySnapshot(feature.id, { process: stream?.process_label });
            if (!ok) {
                setSnapshotError(apiErrorMessage(status, data, 'Could not compute a capability snapshot.'));
                return;
            }
            setSnapshot(data);
        } catch (e) {
            setSnapshotError('Network error computing the snapshot.');
        } finally {
            setSnapshotLoading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ mr: 'auto' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        {feature?.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                        {[
                            feature?.lsl !== null && feature?.lsl !== undefined ? `LSL ${feature.lsl}` : null,
                            feature?.nominal !== null && feature?.nominal !== undefined ? `Target ${feature.nominal}` : null,
                            feature?.usl !== null && feature?.usl !== undefined ? `USL ${feature.usl}` : null,
                            feature?.unit || null,
                        ].filter(Boolean).join(' · ') || 'No spec set'}
                    </Typography>
                </Box>
                {pctInSpec !== null && (
                    <Chip
                        label={`${pctInSpec.toFixed(0)}% in spec`}
                        size="small"
                        sx={{
                            backgroundColor: pctInSpec >= 100 ? 'rgba(129,199,132,0.15)' : 'rgba(255,183,77,0.15)',
                            color: pctInSpec >= 100 ? '#81c784' : '#ffb74d',
                            fontWeight: 700,
                        }}
                    />
                )}
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => onRecordMeasurements?.(feature, stream)}
                >
                    Record measurements
                </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.3)' }} />
                </Box>
            ) : measurements.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        No readings yet. Record your first measurement to see it here.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {chartValues.length >= 2 && (
                        <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                            <GuidedEntryRunChart values={chartValues} specLimits={specLimits} />
                        </Paper>
                    )}

                    <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Time', 'Value', 'Verdict'].map((h) => <TableCell key={h} sx={headCellSx}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentFirst.map((m) => (
                                    <TableRow key={m.id} sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', py: 0.75, px: 1.5, whiteSpace: 'nowrap' }}>
                                            {m.measuredAt ? new Date(m.measuredAt).toLocaleString() : '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', py: 0.75, px: 1.5 }}>
                                            {m.value !== null && m.value !== undefined ? m.value : (m.attrValue ?? '—')}
                                        </TableCell>
                                        <TableCell sx={{ py: 0.75, px: 1.5 }}>
                                            <VerdictChip verdict={mapConformance(m.conformance)} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* ---- Windowed capability snapshot — separate, explicit, manual action. ---- */}
            <Paper sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsightsIcon sx={{ fontSize: 18, color: 'rgba(144,202,249,0.6)' }} />
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, mr: 'auto' }}>
                        Capability snapshot (optional)
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleRunSnapshot}
                        disabled={snapshotLoading || measurements.length < 2}
                        startIcon={snapshotLoading ? <CircularProgress size={12} color="inherit" /> : undefined}
                        sx={{ color: '#90caf9', borderColor: 'rgba(144,202,249,0.4)' }}
                    >
                        {snapshotLoading ? 'Computing…' : 'Run snapshot'}
                    </Button>
                </Box>
                {snapshotError && <Alert severity="error" sx={{ py: 0.5 }}>{snapshotError}</Alert>}
                {snapshot && (
                    // Real POST /api/features/:id/snapshot response is FLAT
                    // ({datasetId, cp, cpk, pp, ppk, n, windowFromId, windowToId} —
                    // backend/measurementRoutes.js snapshot route; pinned by
                    // frontend/src/components/parts/__fixtures__/partsContract.json
                    // "snapshot.response") — never a nested `results` object.
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {['cp', 'cpk', 'pp', 'ppk'].map((key) => (
                            snapshot[key] !== undefined && (
                                <Box key={key}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>{key}</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
                                        {fmtNum(snapshot[key], 3)}
                                    </Typography>
                                </Box>
                            )
                        ))}
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default FeatureInspectionView;
